import React, { useState, useEffect, useRef, useCallback } from 'react';
import { format, formatDistanceToNow } from 'date-fns';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  X,
  Send,
  Paperclip,
  Loader2,
  CheckCheck,
  FileText,
  Image as ImageIcon,
  Star,
  ChevronLeft,
  Search,
  MoreHorizontal,
  Smile,
  ArrowRight,
  Home,
  MessageCircle,
} from 'lucide-react';
import {
  MessageItem,
  ConversationItem,
  ConversationDetail,
  SupportSenderType,
  SupportConversationStatus,
} from './types';

// URL regex pattern for detecting links in text
// Matches URLs with protocol (http/https) or common domain patterns (www. or domain.tld)
const URL_REGEX = /https?:\/\/[^\s<]+[^<.,:;"')\]\s]|www\.[^\s<]+[^<.,:;"')\]\s]|[a-zA-Z0-9][-a-zA-Z0-9]*\.(?:com|org|net|io|co|app|dev|ai|gg|me|tv|info|biz|edu|gov)(?:\/[^\s<]*[^<.,:;"')\]\s])?/gi;

// Component to render text with clickable links
function LinkifiedText({ text, isCustomer }: { text: string; isCustomer?: boolean }) {
  // Use matchAll to find all URLs and their positions
  const matches = Array.from(text.matchAll(new RegExp(URL_REGEX.source, 'gi')));
  
  if (matches.length === 0) {
    return <>{text}</>;
  }
  
  const result: React.ReactNode[] = [];
  let lastIndex = 0;
  
  matches.forEach((match, index) => {
    const url = match[0];
    const startIndex = match.index!;
    
    // Add text before this URL
    if (startIndex > lastIndex) {
      result.push(<span key={`text-${index}`}>{text.slice(lastIndex, startIndex)}</span>);
    }
    
    // Add https:// if no protocol specified
    const href = url.startsWith('http') ? url : `https://${url}`;
    result.push(
      <a
        key={`link-${index}`}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(
          "underline break-all",
          isCustomer 
            ? "text-primary-foreground/90 hover:text-primary-foreground" 
            : "text-blue-500 hover:text-blue-400"
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {url}
      </a>
    );
    
    lastIndex = startIndex + url.length;
  });
  
  // Add any remaining text after the last URL
  if (lastIndex < text.length) {
    result.push(<span key="text-end">{text.slice(lastIndex)}</span>);
  }
  
  return <>{result}</>;
}
import {
  useActiveConversation,
  useStartConversation,
  useSendCustomerMessage,
  useUploadCustomerAttachments,
  useUploadAttachmentsForNewConversation,
  useSubmitCsatRating,
  useCustomerConversations,
  useCustomerConversation,
  useSupportChatInit,
  OnlineSupportAgent,
} from './useSupportChat';
import { useSupportChatContext } from './SupportChatContext';

interface SupportChatWidgetProps {
  isOpen: boolean;
  onClose: () => void;
  onMinimize: () => void;
}

// Fallback avatars when no agents are online
const FALLBACK_AVATARS = [
  { displayName: 'Support', avatarUrl: null, status: 'online' as const },
];

type ViewState = 'home' | 'messages' | 'conversation' | 'new-conversation';

export function SupportChatWidget({ isOpen, onClose, onMinimize }: SupportChatWidgetProps) {
  const [view, setView] = useState<ViewState>('home');
  const [messageContent, setMessageContent] = useState('');
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [showCsatForm, setShowCsatForm] = useState(false);
  const [csatRating, setCsatRating] = useState(0);
  const [csatComment, setCsatComment] = useState('');
  const [selectedConversationId, setSelectedConversationId] = useState<number | null>(null);
  const [optimisticMessages, setOptimisticMessages] = useState<MessageItem[]>([]);
  const [newConversationPendingFiles, setNewConversationPendingFiles] = useState<File[]>([]);

  const { data: activeConversation, isLoading: activeConversationLoading, refetch: refetchActive } = useActiveConversation();
  const { data: selectedConversation, isLoading: selectedConversationLoading, refetch: refetchSelected } = useCustomerConversation(selectedConversationId);
  // Only fetch conversations and init data when widget is open
  // These are not needed until the user interacts with the support chat
  const { data: conversations, refetch: refetchConversations } = useCustomerConversations(isOpen);
  // Combined init endpoint fetches business hours + online agents in single request
  const { data: initData } = useSupportChatInit(isOpen);
  const businessHours = initData?.businessHours;
  const onlineAgents = initData?.onlineAgents;
  const startConversation = useStartConversation();
  const sendMessage = useSendCustomerMessage();
  const uploadAttachments = useUploadCustomerAttachments();
  const uploadAttachmentsForNewConversation = useUploadAttachmentsForNewConversation();
  const submitCsat = useSubmitCsatRating();

  // Use real online agents or fallback
  const displayAgents = onlineAgents && onlineAgents.length > 0 ? onlineAgents : FALLBACK_AVATARS;

  const {
    isConnected,
    joinConversation,
    leaveConversation,
    sendTypingIndicator,
    markMessagesRead,
    typingUsers,
    csatRequested,
    clearCsatRequest,
  } = useSupportChatContext();

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const newConversationFileInputRef = useRef<HTMLInputElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Track the last active conversation ID to preserve it when conversation is closed
  // This allows the user to stay in the conversation view and submit CSAT rating
  const lastActiveConversationIdRef = useRef<number | null>(null);
  
  // Update the ref when we have an active conversation
  useEffect(() => {
    if (activeConversation?.id) {
      lastActiveConversationIdRef.current = activeConversation.id;
    }
  }, [activeConversation?.id]);

  // Determine current conversation - use lastActiveConversationIdRef as fallback
  // This preserves the conversation when it's closed by admin while user is viewing
  const fallbackConversationId = view === 'conversation' ? lastActiveConversationIdRef.current : null;
  const conversationId = selectedConversationId || activeConversation?.id || fallbackConversationId;
  
  // When using the fallback ID (conversation was closed), we need to fetch via selectedConversation
  // Set selectedConversationId to the fallback so useCustomerConversation fetches the closed conversation
  useEffect(() => {
    if (!selectedConversationId && !activeConversation?.id && fallbackConversationId && view === 'conversation') {
      // Active conversation was closed - switch to using selectedConversationId to fetch the closed conversation
      setSelectedConversationId(fallbackConversationId);
    }
  }, [selectedConversationId, activeConversation?.id, fallbackConversationId, view]);
  
  const currentConversationData = selectedConversationId ? selectedConversation : activeConversation;
  const isLoading = selectedConversationId ? selectedConversationLoading : activeConversationLoading;
  
  // Combine real messages with optimistic ones
  const realMessages = currentConversationData?.messages || [];
  const allMessages = [...realMessages, ...optimisticMessages.filter(om => 
    !realMessages.some(rm => rm.id === om.id || (rm.content === om.content && rm.senderType === om.senderType))
  )];

  const hasActiveConversation = activeConversation && activeConversation.id && activeConversation.status !== SupportConversationStatus.CLOSED;

  // Join/leave conversation room for real-time updates
  useEffect(() => {
    if (conversationId && isOpen && view === 'conversation') {
      joinConversation(conversationId);
      return () => leaveConversation(conversationId);
    }
  }, [conversationId, isOpen, view, joinConversation, leaveConversation]);

  // Clear optimistic messages when real messages update
  useEffect(() => {
    if (realMessages.length > 0) {
      setOptimisticMessages([]);
    }
  }, [realMessages.length]);

  // Mark admin messages as read when customer views them
  useEffect(() => {
    if (conversationId && isOpen && view === 'conversation' && realMessages.length > 0) {
      const unreadAdminMessageIds = realMessages
        .filter(m => m.senderType === SupportSenderType.ADMIN && !m.readAt)
        .map(m => m.id);
      
      if (unreadAdminMessageIds.length > 0) {
        markMessagesRead(conversationId, unreadAdminMessageIds);
      }
    }
  }, [conversationId, isOpen, view, realMessages, markMessagesRead]);

  // Scroll to bottom on new messages
  useEffect(() => {
    if (view === 'conversation' && allMessages.length > 0) {
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [allMessages.length, view]);

  // Show CSAT form when requested
  // Check against both conversationId and lastActiveConversationIdRef to handle race conditions
  // when the conversation is closed and activeConversation becomes null
  // Use Number() conversion to handle string/number type mismatches from API responses
  useEffect(() => {
    if (csatRequested && (Number(csatRequested) === Number(conversationId) || Number(csatRequested) === Number(lastActiveConversationIdRef.current))) {
      setShowCsatForm(true);
    }
  }, [csatRequested, conversationId]);

  // Handle typing indicator with debounce
  const handleTyping = useCallback(() => {
    if (!conversationId) return;

    sendTypingIndicator(conversationId, true);

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    typingTimeoutRef.current = setTimeout(() => {
      sendTypingIndicator(conversationId, false);
    }, 2000);
  }, [conversationId, sendTypingIndicator]);

  const handleStartConversation = async () => {
    if (!messageContent.trim() && newConversationPendingFiles.length === 0) return;

    try {
      let attachmentIds: number[] = [];

      // Upload files first if any
      if (newConversationPendingFiles.length > 0) {
        const result = await uploadAttachmentsForNewConversation.mutateAsync({
          files: newConversationPendingFiles,
        });
        attachmentIds = result.attachments.map((a) => a.id);
      }

      const newConversation = await startConversation.mutateAsync({
        message: messageContent.trim(),
        attachmentIds: attachmentIds.length > 0 ? attachmentIds : undefined,
      });
      
      // Clear any stale conversation state and set the new conversation
      setSelectedConversationId(null);
      lastActiveConversationIdRef.current = newConversation.id;
      setOptimisticMessages([]);
      setMessageContent('');
      setNewConversationPendingFiles([]);
      setView('conversation');
    } catch (error) {
      console.error('Failed to start conversation:', error);
    }
  };

  const handleNewConversationFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const validFiles = files.filter(
      (f) => f.type.startsWith('image/') && f.size <= 2 * 1024 * 1024
    );
    setNewConversationPendingFiles((prev) => [...prev, ...validFiles].slice(0, 5));
    e.target.value = '';
  };

  const handleSendMessage = async () => {
    if (!conversationId || (!messageContent.trim() && pendingFiles.length === 0)) return;

    const content = messageContent.trim();
    const tempId = Date.now();
    
    // Add optimistic message immediately
    const optimisticMessage: MessageItem = {
      id: tempId,
      conversationId,
      senderType: SupportSenderType.CUSTOMER,
      senderId: 0,
      senderName: 'You',
      content,
      attachments: [],
      readAt: null,
      createdAt: new Date().toISOString(),
    };
    
    setOptimisticMessages(prev => [...prev, optimisticMessage]);
    setMessageContent('');
    
    // Stop typing indicator
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    sendTypingIndicator(conversationId, false);

    try {
      let attachmentIds: number[] = [];

      if (pendingFiles.length > 0) {
        const result = await uploadAttachments.mutateAsync({
          conversationId,
          files: pendingFiles,
        });
        attachmentIds = result.attachments.map((a) => a.id);
      }

      await sendMessage.mutateAsync({
        conversationId,
        content,
        attachmentIds: attachmentIds.length > 0 ? attachmentIds : undefined,
      });

      setPendingFiles([]);
      
      // Refetch to get the real message
      if (selectedConversationId) {
        refetchSelected();
      } else {
        refetchActive();
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      // Remove optimistic message on error
      setOptimisticMessages(prev => prev.filter(m => m.id !== tempId));
      setMessageContent(content); // Restore message
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (view === 'new-conversation') {
        handleStartConversation();
      } else if (view === 'conversation') {
        handleSendMessage();
      }
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const validFiles = files.filter(
      (f) => f.type.startsWith('image/') && f.size <= 2 * 1024 * 1024
    );
    setPendingFiles((prev) => [...prev, ...validFiles].slice(0, 5));
    e.target.value = '';
  };

  const handleSubmitCsat = async () => {
    if (!conversationId || csatRating === 0) return;

    try {
      await submitCsat.mutateAsync({
        conversationId,
        rating: csatRating,
        comment: csatComment.trim() || undefined,
      });
      setShowCsatForm(false);
      setCsatRating(0);
      setCsatComment('');
      clearCsatRequest();
    } catch (error) {
      console.error('Failed to submit CSAT:', error);
    }
  };

  const openConversation = (id: number | string) => {
    // Ensure ID is always a number for consistent query keys
    const numericId = typeof id === 'string' ? parseInt(id, 10) : id;
    setSelectedConversationId(numericId);
    setOptimisticMessages([]);
    setView('conversation');
  };

  const goBack = () => {
    setSelectedConversationId(null);
    setOptimisticMessages([]);
    setView('messages');
    refetchConversations();
  };

  const currentTypingUsers = conversationId ? typingUsers[conversationId] || [] : [];
  const adminTyping = currentTypingUsers.filter((u) => u.userType === 'admin');
  const unreadCount = conversations?.reduce((acc, c) => acc + (c.unreadCount || 0), 0) || 0;

  if (!isOpen) return null;

  return (
    <div 
      className={cn(
        "fixed bottom-[calc(5rem+env(safe-area-inset-bottom))] right-2 w-[370px] bg-card rounded-2xl shadow-2xl flex flex-col overflow-hidden z-[260] border border-border md:bottom-4 md:right-4 md:z-50",
        "transform transition-all duration-300 ease-out",
        "animate-in slide-in-from-bottom-5 fade-in-0"
      )}
      style={{ 
        height: '560px',
        maxHeight: 'calc(100vh - 100px)',
      }}
    >
      {/* Render based on view state */}
      {view === 'home' && (
        <HomeView
          onClose={onClose}
          onStartChat={() => setView('new-conversation')}
          onViewMessages={() => setView('messages')}
          hasActiveConversation={hasActiveConversation}
          unreadCount={unreadCount}
          businessHours={businessHours}
          isConnected={isConnected}
          agents={displayAgents}
        />
      )}

      {view === 'messages' && (
        <MessagesListView
          conversations={conversations || []}
          onBack={() => setView('home')}
          onClose={onClose}
          onSelectConversation={openConversation}
          onNewMessage={() => setView('new-conversation')}
          hasActiveConversation={hasActiveConversation}
        />
      )}

      {view === 'conversation' && (
        <ConversationView
          conversation={currentConversationData as ConversationDetail | null}
          messages={allMessages}
          isLoading={isLoading}
          messageContent={messageContent}
          pendingFiles={pendingFiles}
          adminTyping={adminTyping}
          showCsatForm={showCsatForm}
          csatRating={csatRating}
          csatComment={csatComment}
          isClosed={currentConversationData?.status === SupportConversationStatus.CLOSED}
          onBack={goBack}
          onClose={onClose}
          onMessageChange={(value) => {
            setMessageContent(value);
            handleTyping();
          }}
          onKeyDown={handleKeyDown}
          onSend={handleSendMessage}
          onFileSelect={handleFileSelect}
          onRemoveFile={(index) => setPendingFiles((prev) => prev.filter((_, i) => i !== index))}
          fileInputRef={fileInputRef}
          messagesEndRef={messagesEndRef}
          isSending={sendMessage.isPending || uploadAttachments.isPending}
          onCsatRatingChange={setCsatRating}
          onCsatCommentChange={setCsatComment}
          onCsatSubmit={handleSubmitCsat}
          onCsatSkip={() => {
            setShowCsatForm(false);
            clearCsatRequest();
          }}
          isCsatSubmitting={submitCsat.isPending}
        />
      )}

      {view === 'new-conversation' && (
        <NewConversationView
          messageContent={messageContent}
          pendingFiles={newConversationPendingFiles}
          onBack={() => setView('home')}
          onClose={onClose}
          onMessageChange={setMessageContent}
          onKeyDown={handleKeyDown}
          onSend={handleStartConversation}
          onFileSelect={handleNewConversationFileSelect}
          onRemoveFile={(index) => setNewConversationPendingFiles((prev) => prev.filter((_, i) => i !== index))}
          fileInputRef={newConversationFileInputRef}
          isSending={startConversation.isPending || uploadAttachmentsForNewConversation.isPending}
          agents={displayAgents}
        />
      )}

      {/* Bottom Navigation - only show on home and messages */}
      {(view === 'home' || view === 'messages') && (
        <BottomNav
          currentView={view}
          onNavigate={setView}
          unreadCount={unreadCount}
        />
      )}
    </div>
  );
}

// =============================================================================
// HOME VIEW
// =============================================================================

function HomeView({
  onClose,
  onStartChat,
  onViewMessages,
  hasActiveConversation,
  unreadCount,
  businessHours,
  isConnected,
  agents,
}: {
  onClose: () => void;
  onStartChat: () => void;
  onViewMessages: () => void;
  hasActiveConversation: boolean;
  unreadCount: number;
  businessHours: any;
  isConnected: boolean;
  agents: OnlineSupportAgent[];
}) {
  const getInitials = (name: string | null | undefined) => {
    if (!name) return 'S';
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="flex-1 flex flex-col">
      {/* Header with gradient - uses primary color */}
      <div className="relative px-5 pt-5 pb-6 bg-primary">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-primary-foreground/10 transition-colors"
        >
          <X className="h-5 w-5 text-primary-foreground/80" />
        </button>

        {/* Team Avatars */}
        <div className="flex -space-x-2 mb-4">
          {agents.slice(0, 3).map((agent, i) => (
            <Avatar 
              key={i} 
              className="w-10 h-10 border-2 border-primary-foreground/20 ring-2 ring-transparent hover:ring-primary-foreground/30 transition-all"
            >
              <AvatarImage src={agent.avatarUrl || undefined} alt={agent.displayName} />
              <AvatarFallback className="bg-gradient-to-br from-blue-400 to-purple-500 text-white text-sm">
                {getInitials(agent.displayName)}
              </AvatarFallback>
            </Avatar>
          ))}
          {/* Online indicator */}
          <div className="flex items-center ml-3">
            <span className={cn(
              "w-2 h-2 rounded-full mr-1.5",
              isConnected && agents.length > 0 ? "bg-green-400" : "bg-muted-foreground"
            )} />
            <span className="text-xs text-primary-foreground/60">
              {isConnected && agents.length > 0 ? 'Online' : 'Connecting...'}
            </span>
          </div>
        </div>

        {/* Greeting */}
        <h1 className="text-xl font-semibold text-primary-foreground mb-0.5">
          Hi there 👋
        </h1>
        <p className="text-base text-primary-foreground/70">
          How can we help?
        </p>
      </div>

      {/* Content */}
      <div className="flex-1 px-4 pt-4 space-y-3 overflow-y-auto pb-20 bg-background">
        {/* Send Message Card - only show if no active conversation */}
        {!hasActiveConversation && (
          <button
            onClick={onStartChat}
            className={cn(
              "w-full bg-card rounded-xl p-4 shadow-md border border-border",
              "flex items-center gap-3 text-left",
              "hover:shadow-lg hover:scale-[1.01] active:scale-[0.99]",
              "transition-all duration-200"
            )}
          >
            <div className="flex-1">
              <h3 className="font-semibold text-foreground text-sm">Send us a message</h3>
              <p className="text-xs text-muted-foreground">
                {businessHours?.isOpen 
                  ? "We typically reply in a few minutes" 
                  : "We'll get back to you soon"}
              </p>
            </div>
            <div className="w-9 h-9 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
              <ArrowRight className="h-4 w-4 text-primary-foreground" />
            </div>
          </button>
        )}

        {/* Active conversation indicator */}
        {hasActiveConversation && (
          <button
            onClick={onViewMessages}
            className={cn(
              "w-full bg-card rounded-xl p-4 shadow-md border border-border",
              "flex items-center gap-3 text-left",
              "hover:shadow-lg hover:scale-[1.01] active:scale-[0.99]",
              "transition-all duration-200"
            )}
          >
            <div className="flex-1">
              <h3 className="font-semibold text-foreground text-sm">Continue conversation</h3>
              <p className="text-xs text-muted-foreground">You have an active chat</p>
            </div>
            <div className="relative w-9 h-9 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
              <ArrowRight className="h-4 w-4 text-primary-foreground" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-destructive text-destructive-foreground text-[10px] font-bold rounded-full flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </div>
          </button>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// MESSAGES LIST VIEW
// =============================================================================

function MessagesListView({
  conversations,
  onBack,
  onClose,
  onSelectConversation,
  onNewMessage,
  hasActiveConversation,
}: {
  conversations: ConversationItem[];
  onBack: () => void;
  onClose: () => void;
  onSelectConversation: (id: number) => void;
  onNewMessage: () => void;
  hasActiveConversation: boolean;
}) {
  return (
    <div className="flex-1 flex flex-col min-h-0 bg-background">
      {/* Header */}
      <div className="px-4 py-3 border-b border-border flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-2">
          <button
            onClick={onBack}
            className="p-1.5 -ml-1 rounded-full hover:bg-muted transition-colors"
          >
            <ChevronLeft className="h-5 w-5 text-muted-foreground" />
          </button>
          <h2 className="text-base font-semibold text-foreground">Messages</h2>
        </div>
        <button
          onClick={onClose}
          className="p-1.5 rounded-full hover:bg-muted transition-colors"
        >
          <X className="h-5 w-5 text-muted-foreground" />
        </button>
      </div>

      {/* Conversations List */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {conversations.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center px-6">
            <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center mb-3">
              <MessageCircle className="h-7 w-7 text-muted-foreground" />
            </div>
            <p className="text-muted-foreground text-sm mb-3">No messages yet</p>
            {!hasActiveConversation && (
              <Button onClick={onNewMessage} className="rounded-full text-sm" size="sm">
                Start a conversation
              </Button>
            )}
          </div>
        ) : (
          conversations.map((conv) => {
            const isClosed = conv.status === SupportConversationStatus.CLOSED;
            const isActive = conv.status === SupportConversationStatus.OPEN || conv.status === SupportConversationStatus.SNOOZED;
            const displayName =
              (conv as ConversationDetail).assignedAdminDisplayName ||
              conv.assignedAdminUsername ||
              'GM';

            const initials = displayName
              .split(' ')
              .map((n: string) => n[0])
              .join('')
              .toUpperCase()
              .slice(0, 2);

            return (
              <button
                key={conv.id}
                onClick={() => onSelectConversation(conv.id)}
                className={cn(
                  "w-full px-4 py-3 flex items-start gap-3 text-left",
                  "hover:bg-muted/50 transition-colors",
                  "border-b border-border/50",
                  conv.unreadCount > 0 && "bg-primary/5",
                  isClosed && "opacity-60"
                )}
              >
                {/* Avatar with status indicator */}
                <div className="relative flex-shrink-0">
                  <Avatar className={cn("w-10 h-10", isClosed && "grayscale")}>
                    {conv.assignedAdminAvatarUrl && (
                      <AvatarImage src={conv.assignedAdminAvatarUrl} alt={displayName} />
                    )}
                    <AvatarFallback className="bg-gradient-to-br from-amber-400 to-orange-500 text-white text-sm">
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                  {/* Status dot */}
                  <span className={cn(
                    "absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-background",
                    isActive ? "bg-green-500" : "bg-muted-foreground/50"
                  )} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-0.5">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <span className={cn(
                        "truncate text-sm",
                        conv.unreadCount > 0 ? "font-semibold text-foreground" : "font-medium text-foreground/80",
                        isClosed && "text-muted-foreground"
                      )}>
                        {displayName}
                      </span>
                      {isClosed && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground flex-shrink-0">
                          Closed
                        </span>
                      )}
                    </div>
                    <span className="text-xs text-muted-foreground flex-shrink-0 ml-2">
                      {formatDistanceToNow(new Date(conv.lastMessageAt), { addSuffix: false })}
                    </span>
                  </div>
                  <p className={cn(
                    "text-xs truncate",
                    conv.unreadCount > 0 ? "text-foreground/80 font-medium" : "text-muted-foreground"
                  )}>
                    {conv.lastMessagePreview}
                  </p>
                </div>
                {conv.unreadCount > 0 && (
                  <span className="w-5 h-5 bg-primary text-primary-foreground text-xs font-bold rounded-full flex items-center justify-center flex-shrink-0">
                    {conv.unreadCount}
                  </span>
                )}
              </button>
            );
          })
        )}
      </div>

      {/* New Message Button - only show if no active conversation */}
      {conversations.length > 0 && !hasActiveConversation && (
        <div className="flex-shrink-0 px-4 py-3 border-t border-border">
          <Button 
            onClick={onNewMessage}
            className="w-full rounded-full text-sm"
          >
            <Send className="h-4 w-4 mr-2" />
            New message
          </Button>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// CONVERSATION VIEW
// =============================================================================

function ConversationView({
  conversation,
  messages,
  isLoading,
  messageContent,
  pendingFiles,
  adminTyping,
  showCsatForm,
  csatRating,
  csatComment,
  isClosed,
  onBack,
  onClose,
  onMessageChange,
  onKeyDown,
  onSend,
  onFileSelect,
  onRemoveFile,
  fileInputRef,
  messagesEndRef,
  isSending,
  onCsatRatingChange,
  onCsatCommentChange,
  onCsatSubmit,
  onCsatSkip,
  isCsatSubmitting,
}: {
  conversation: ConversationDetail | null | undefined;
  messages: MessageItem[];
  isLoading: boolean;
  messageContent: string;
  pendingFiles: File[];
  adminTyping: any[];
  showCsatForm: boolean;
  csatRating: number;
  csatComment: string;
  isClosed?: boolean;
  onBack: () => void;
  onClose: () => void;
  onMessageChange: (value: string) => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  onSend: () => void;
  onFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveFile: (index: number) => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
  messagesEndRef: React.RefObject<HTMLDivElement>;
  isSending: boolean;
  onCsatRatingChange: (rating: number) => void;
  onCsatCommentChange: (comment: string) => void;
  onCsatSubmit: () => void;
  onCsatSkip: () => void;
  isCsatSubmitting: boolean;
}) {
  const headerName =
    conversation?.assignedAdminDisplayName ||
    conversation?.assignedAdminUsername ||
    'GM';
  const headerAvatarUrl = conversation?.assignedAdminAvatarUrl || null;

  if (showCsatForm) {
    return (
      <CsatForm
        rating={csatRating}
        comment={csatComment}
        onRatingChange={onCsatRatingChange}
        onCommentChange={onCsatCommentChange}
        onSubmit={onCsatSubmit}
        onSkip={onCsatSkip}
        isSubmitting={isCsatSubmitting}
      />
    );
  }

  return (
    <div className="flex-1 flex flex-col min-h-0 bg-background">
      {/* Header */}
      <div className="px-3 py-2.5 border-b border-border flex items-center gap-2 flex-shrink-0">
        <button
          onClick={onBack}
          className="p-1.5 -ml-1 rounded-full hover:bg-muted transition-colors"
        >
          <ChevronLeft className="h-5 w-5 text-muted-foreground" />
        </button>
        <Avatar className="w-7 h-7">
          {headerAvatarUrl && (
            <AvatarImage src={headerAvatarUrl} alt={headerName} />
          )}
          <AvatarFallback className="bg-gradient-to-br from-amber-400 to-orange-500 text-white text-xs">
            {(headerName || 'GM')
              .split(' ')
              .map((n) => n[0])
              .join('')
              .toUpperCase()
              .slice(0, 2)}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-foreground text-sm leading-tight">{headerName}</h3>
          <p className="text-xs text-muted-foreground leading-tight">
            {adminTyping.length > 0 ? (
              <span className="text-green-500 animate-pulse">Typing...</span>
            ) : (
              'Support Team'
            )}
          </p>
        </div>
        <button
          onClick={onClose}
          className="p-1.5 rounded-full hover:bg-muted transition-colors"
        >
          <X className="h-5 w-5 text-muted-foreground" />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-3 py-3 min-h-0">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <p className="text-sm text-muted-foreground">Start the conversation!</p>
          </div>
        ) : (
          <div className="space-y-3">
            {messages.map((message, index) => (
              <MessageBubble 
                key={message.id} 
                message={message}
                showAvatar={
                  message.senderType !== SupportSenderType.CUSTOMER &&
                  (index === 0 || messages[index - 1]?.senderType === SupportSenderType.CUSTOMER)
                }
              />
            ))}

            {/* Typing Indicator */}
            {adminTyping.length > 0 && (
              <div className="flex items-start gap-2">
                <Avatar className="w-7 h-7">
                  <AvatarFallback className="bg-muted text-muted-foreground text-xs">S</AvatarFallback>
                </Avatar>
                <div className="bg-muted rounded-2xl rounded-bl-md px-3 py-2">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" />
                    <span className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0.15s' }} />
                    <span className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0.3s' }} />
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Composer or Closed Message */}
      <div className="flex-shrink-0 px-3 py-2 border-t border-border bg-card">
        {isClosed ? (
          /* Closed conversation notice */
          <div className="flex items-center justify-center gap-2 py-2 text-muted-foreground">
            <CheckCheck className="h-4 w-4" />
            <span className="text-sm">This conversation has been resolved</span>
          </div>
        ) : (
          <>
            {/* Pending Files */}
            {pendingFiles.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {pendingFiles.map((file, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-1 px-2 py-1 bg-muted rounded-md text-xs"
                  >
                    <ImageIcon className="h-3 w-3 text-muted-foreground" />
                    <span className="truncate max-w-[80px] text-foreground">{file.name}</span>
                    <button
                      onClick={() => onRemoveFile(index)}
                      className="p-0.5 hover:bg-muted-foreground/20 rounded"
                    >
                      <X className="h-3 w-3 text-muted-foreground" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="flex items-end gap-2">
              <div className="flex-1 bg-muted rounded-2xl overflow-hidden">
                <textarea
                  placeholder="Type a message..."
                  value={messageContent}
                  onChange={(e) => onMessageChange(e.target.value)}
                  onKeyDown={onKeyDown}
                  className="w-full px-4 py-2.5 text-base md:text-sm resize-none focus:outline-none min-h-[40px] max-h-[100px] bg-transparent text-foreground placeholder:text-muted-foreground"
                  rows={1}
                />
              </div>
              <div className="flex items-center gap-1 pb-1">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={onFileSelect}
                  multiple
                  accept="image/*"
                  className="hidden"
                />
                <button 
                  onClick={() => fileInputRef.current?.click()}
                  className="p-2 rounded-full hover:bg-muted transition-colors"
                >
                  <Paperclip className="h-5 w-5 text-muted-foreground" />
                </button>
                <button
                  onClick={onSend}
                  disabled={(!messageContent.trim() && pendingFiles.length === 0) || isSending}
                  className={cn(
                    "p-2 rounded-full transition-all",
                    messageContent.trim() || pendingFiles.length > 0
                      ? "bg-primary text-primary-foreground hover:bg-primary/90"
                      : "bg-muted text-muted-foreground"
                  )}
                >
                  {isSending ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <Send className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// NEW CONVERSATION VIEW
// =============================================================================

function NewConversationView({
  messageContent,
  pendingFiles,
  onBack,
  onClose,
  onMessageChange,
  onKeyDown,
  onSend,
  onFileSelect,
  onRemoveFile,
  fileInputRef,
  isSending,
  agents,
}: {
  messageContent: string;
  pendingFiles: File[];
  onBack: () => void;
  onClose: () => void;
  onMessageChange: (value: string) => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  onSend: () => void;
  onFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveFile: (index: number) => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
  isSending: boolean;
  agents: OnlineSupportAgent[];
}) {
  const getInitials = (name: string | null | undefined) => {
    if (!name) return 'S';
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="flex-1 flex flex-col min-h-0 bg-background">
      {/* Header */}
      <div className="px-3 py-2.5 border-b border-border flex items-center gap-2 flex-shrink-0">
        <button
          onClick={onBack}
          className="p-1.5 -ml-1 rounded-full hover:bg-muted transition-colors"
        >
          <ChevronLeft className="h-5 w-5 text-muted-foreground" />
        </button>
        <div className="flex-1">
          <h3 className="font-semibold text-foreground text-sm">New message</h3>
        </div>
        <button
          onClick={onClose}
          className="p-1.5 rounded-full hover:bg-muted transition-colors"
        >
          <X className="h-5 w-5 text-muted-foreground" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col p-4">
        <div className="flex-1 flex flex-col justify-center items-center text-center px-4">
          <div className="flex -space-x-2 mb-3">
            {agents.slice(0, 3).map((agent, i) => (
              <Avatar key={i} className="w-10 h-10 border-2 border-border shadow-sm">
                <AvatarImage src={agent.avatarUrl || undefined} alt={agent.displayName} />
                <AvatarFallback className="bg-gradient-to-br from-blue-400 to-purple-500 text-white text-xs">
                  {getInitials(agent.displayName)}
                </AvatarFallback>
              </Avatar>
            ))}
          </div>
          <h2 className="text-base font-semibold text-foreground mb-0.5">
            Start a conversation
          </h2>
          <p className="text-xs text-muted-foreground mb-4">
            We typically reply in a few minutes
          </p>
        </div>

        {/* Pending Files */}
        {pendingFiles.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-2 px-1">
            {pendingFiles.map((file, index) => (
              <div
                key={index}
                className="flex items-center gap-1 px-2 py-1 bg-muted rounded-md text-xs"
              >
                <ImageIcon className="h-3 w-3 text-muted-foreground" />
                <span className="truncate max-w-[80px] text-foreground">{file.name}</span>
                <button
                  onClick={() => onRemoveFile(index)}
                  className="p-0.5 hover:bg-muted-foreground/20 rounded"
                >
                  <X className="h-3 w-3 text-muted-foreground" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Composer */}
        <div className="flex items-end gap-2">
          <div className="flex-1 bg-muted rounded-2xl overflow-hidden">
            <textarea
              placeholder="How can we help?"
              value={messageContent}
              onChange={(e) => onMessageChange(e.target.value)}
              onKeyDown={onKeyDown}
              className="w-full px-4 py-3 text-base md:text-sm resize-none focus:outline-none min-h-[80px] bg-transparent text-foreground placeholder:text-muted-foreground"
              autoFocus
            />
          </div>
          <div className="flex items-center gap-1 pb-1">
            <input
              type="file"
              ref={fileInputRef}
              onChange={onFileSelect}
              multiple
              accept="image/*"
              className="hidden"
            />
            <button 
              onClick={() => fileInputRef.current?.click()}
              className="p-2 rounded-full hover:bg-muted transition-colors"
              title="Attach image"
            >
              <Paperclip className="h-5 w-5 text-muted-foreground" />
            </button>
            <button
              onClick={onSend}
              disabled={(!messageContent.trim() && pendingFiles.length === 0) || isSending}
              className={cn(
                "p-3 rounded-full transition-all",
                (messageContent.trim() || pendingFiles.length > 0)
                  ? "bg-primary text-primary-foreground hover:bg-primary/90"
                  : "bg-muted text-muted-foreground"
              )}
            >
              {isSending ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <Send className="h-5 w-5" />
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// BOTTOM NAVIGATION
// =============================================================================

function BottomNav({
  currentView,
  onNavigate,
  unreadCount,
}: {
  currentView: ViewState;
  onNavigate: (view: ViewState) => void;
  unreadCount: number;
}) {
  return (
    <div className="flex-shrink-0 bg-card border-t border-border px-6 py-2 flex items-center justify-around">
      <button
        onClick={() => onNavigate('home')}
        className={cn(
          "flex flex-col items-center gap-0.5 py-1.5 px-4 rounded-lg transition-colors",
          currentView === 'home' ? "text-foreground" : "text-muted-foreground hover:text-foreground/80"
        )}
      >
        <Home className="h-5 w-5" />
        <span className="text-[10px] font-medium">Home</span>
      </button>
      <button
        onClick={() => onNavigate('messages')}
        className={cn(
          "flex flex-col items-center gap-0.5 py-1.5 px-4 rounded-lg transition-colors relative",
          currentView === 'messages' ? "text-foreground" : "text-muted-foreground hover:text-foreground/80"
        )}
      >
        <div className="relative">
          <MessageCircle className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1.5 w-4 h-4 bg-destructive text-destructive-foreground text-[10px] font-bold rounded-full flex items-center justify-center">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </div>
        <span className="text-[10px] font-medium">Messages</span>
      </button>
    </div>
  );
}

// =============================================================================
// MESSAGE BUBBLE
// =============================================================================

function MessageBubble({ message, showAvatar }: { message: MessageItem; showAvatar: boolean }) {
  const isCustomer = message.senderType === SupportSenderType.CUSTOMER;
  const isSystem = message.senderType === SupportSenderType.SYSTEM;
  const isOptimistic = message.id > 1000000000000; // Optimistic messages have timestamp IDs

  if (isSystem) {
    return (
      <div className="flex justify-center my-3">
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground bg-muted px-3 py-1 rounded-full">
          <span><LinkifiedText text={message.content} /></span>
        </div>
      </div>
    );
  }

  return (
    <div className={cn(
      'flex items-end gap-1.5',
      isCustomer ? 'flex-row-reverse' : 'flex-row',
      isOptimistic && 'opacity-70'
    )}>
      {!isCustomer && showAvatar ? (
        <Avatar className="w-7 h-7 flex-shrink-0">
          {message.senderAvatarUrl && (
            <AvatarImage src={message.senderAvatarUrl} alt={message.senderName} />
          )}
          <AvatarFallback className="bg-muted text-muted-foreground text-xs">
            {(message.senderName || 'S')
              .split(' ')
              .map((n) => n[0])
              .join('')
              .toUpperCase()
              .slice(0, 2)}
          </AvatarFallback>
        </Avatar>
      ) : !isCustomer ? (
        <div className="w-7" />
      ) : null}

      <div className="flex flex-col gap-0.5 max-w-[75%]">
        {!isCustomer && showAvatar && (
          <span className="text-[10px] text-muted-foreground ml-1">{message.senderName}</span>
        )}
        <div
          className={cn(
            'px-3 py-2 rounded-2xl',
            isCustomer
              ? 'bg-primary text-primary-foreground rounded-br-md'
              : 'bg-muted text-foreground rounded-bl-md'
          )}
        >
          <p className="text-sm whitespace-pre-wrap leading-snug">
            <LinkifiedText text={message.content} isCustomer={isCustomer} />
          </p>

          {/* Attachments */}
          {message.attachments && message.attachments.length > 0 && (
            <div className="mt-1.5 space-y-1">
              {message.attachments.map((attachment) => (
                <AttachmentPreview
                  key={attachment.id}
                  attachment={attachment}
                  isCustomer={isCustomer}
                />
              ))}
            </div>
          )}
        </div>
        <div className={cn('flex items-center gap-1 px-1', isCustomer ? 'justify-end' : 'justify-start')}>
          <span className="text-[10px] text-muted-foreground">
            {isOptimistic ? 'Sending...' : format(new Date(message.createdAt), 'h:mm a')}
          </span>
          {isCustomer && !isOptimistic && message.readAt && (
            <CheckCheck className="h-3 w-3 text-primary" />
          )}
          {isCustomer && !isOptimistic && !message.readAt && (
            <CheckCheck className="h-3 w-3 text-muted-foreground/50" />
          )}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// ATTACHMENT PREVIEW
// =============================================================================

function AttachmentPreview({
  attachment,
  isCustomer,
}: {
  attachment: MessageItem['attachments'][0];
  isCustomer: boolean;
}) {
  const isImage = attachment.fileType.startsWith('image/');

  if (isImage && attachment.url) {
    return (
      <a href={attachment.url} target="_blank" rel="noopener noreferrer">
        <img
          src={attachment.thumbnailUrl || attachment.url}
          alt={attachment.fileName}
          className="max-w-[180px] max-h-[120px] rounded-lg object-cover"
        />
      </a>
    );
  }

  return (
    <a
      href={attachment.url}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        'flex items-center gap-2 p-2 rounded-lg text-xs',
        isCustomer ? 'bg-primary/80' : 'bg-card'
      )}
    >
      <FileText className="h-4 w-4" />
      <span className="truncate max-w-[120px]">{attachment.fileName}</span>
    </a>
  );
}

// =============================================================================
// CSAT FORM
// =============================================================================

function CsatForm({
  rating,
  comment,
  onRatingChange,
  onCommentChange,
  onSubmit,
  onSkip,
  isSubmitting,
}: {
  rating: number;
  comment: string;
  onRatingChange: (rating: number) => void;
  onCommentChange: (comment: string) => void;
  onSubmit: () => void;
  onSkip: () => void;
  isSubmitting: boolean;
}) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center p-5 bg-background">
      <div className="text-center mb-5">
        <h4 className="text-lg font-semibold text-foreground mb-1">
          How was your experience?
        </h4>
        <p className="text-xs text-muted-foreground">
          Your feedback helps us improve
        </p>
      </div>

      {/* Star Rating */}
      <div className="flex gap-2 mb-5">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            onClick={() => onRatingChange(star)}
            className="p-0.5 transition-transform hover:scale-110 active:scale-95"
          >
            <Star
              className={cn(
                'h-8 w-8 transition-colors',
                star <= rating 
                  ? 'fill-amber-400 text-amber-400' 
                  : 'text-muted-foreground/30 hover:text-muted-foreground/50'
              )}
            />
          </button>
        ))}
      </div>

      {/* Comment */}
      <textarea
        placeholder="Any additional feedback? (optional)"
        value={comment}
        onChange={(e) => onCommentChange(e.target.value)}
        className="w-full px-3 py-2.5 bg-muted rounded-lg text-base md:text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring mb-4 text-foreground placeholder:text-muted-foreground"
        rows={2}
      />

      <div className="flex gap-2 w-full">
        <Button 
          variant="outline" 
          className="flex-1 rounded-full text-sm" 
          size="sm"
          onClick={onSkip}
        >
          Skip
        </Button>
        <Button
          className="flex-1 rounded-full text-sm"
          size="sm"
          onClick={onSubmit}
          disabled={rating === 0 || isSubmitting}
        >
          {isSubmitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Submit'}
        </Button>
      </div>
    </div>
  );
}
